import { CalendarRange } from "lucide-react";
import { Link } from "react-router-dom";

import { AvailabilityPatternView } from "@/api/types";
import { cn } from "@/lib/utils";

import { DurationField } from "./DurationField";

/** Quick gaps for the fill interval (0 = back-to-back). Only shown when an interval is applicable. */
const GAP_PRESETS = [
  { label: "None", minutes: 0 },
  { label: "5 min", minutes: 5 },
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
];

/**
 * The body of the "Fill" toolbar popover, shared by both builders. It carries the fill's OWN settings
 * — the slot length ("Duration") and, where it applies, the gap between slots ("Interval") — because
 * those only matter when you fill from a pattern; they don't affect the hand-draw cursor (that's the
 * separate "Cursor duration" toolbar control). Set them here, then click an availability pattern: its weekly
 * hours are laid down as duration-sized slots, spaced by the interval, wherever they don't overlap
 * what's already drawn. Picking a type applies immediately and closes the popover. Clearing lives
 * OUTSIDE this menu, as its own inline pill.
 */
export function FillFromPatternMenu({
  availabilityPatterns,
  selectedId,
  onPick,
  close,
  duration,
  onDurationChange,
  interval,
  onIntervalChange,
}: {
  availabilityPatterns: AvailabilityPatternView[];
  selectedId: string;
  /** Apply this availability pattern to the calendar (select + fill in one step). */
  onPick: (id: string) => void;
  close: () => void;
  /** Fill slot length (minutes) — each laid-down slot is this long. */
  duration: number;
  onDurationChange: (next: number) => void;
  /** Gap between consecutive fill slots (minutes). Omit for ad-hoc, where fill is always back-to-back. */
  interval?: number;
  onIntervalChange?: (next: number) => void;
}) {
  return (
    <div className="w-72 space-y-2.5">
      <div className="space-y-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Slot length
        </span>
        <DurationField minutes={duration} onChange={onDurationChange} min={5} max={480} label="Slot length" />
      </div>
      {onIntervalChange && (
        <div className="space-y-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Gap between slots
          </span>
          <DurationField
            minutes={interval ?? 0}
            onChange={onIntervalChange}
            min={0}
            max={240}
            presets={GAP_PRESETS}
            label="Gap between slots"
          />
        </div>
      )}

      <div className="flex items-center justify-end border-t border-border pt-2">
        <Link
          to="/profile#availability-patterns"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          title="Change an availability pattern's availability"
        >
          <CalendarRange className="h-3 w-3" /> Edit types
        </Link>
      </div>
      <div className="max-h-52 space-y-0.5 overflow-auto">
        {availabilityPatterns.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              onPick(t.id);
              close();
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground transition hover:bg-muted",
              selectedId === t.id && "bg-muted font-semibold",
            )}
          >
            <span className="truncate">
              {t.name}
              {t.is_default ? " (default)" : ""}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
